/**
 * KWS Factory
 * 
 * Creates the appropriate KWS implementation based on availability and configuration.
 * Automatically falls back from Porcupine to TextFallbackKWS if needed.
 */

import { KWSAdapter } from './KWSAdapter.js';
import { PorcupineKWS } from './PorcupineKWS.js';
import { TextFallbackKWS } from './TextFallbackKWS.js';

export class KWSFactory {
    /**
     * Create a KWS adapter with automatic fallback
     * @param options - KWS configuration options
     * @returns Promise<KWSAdapter> - The initialized KWS adapter
     */
    static async createKWS(options = {}) {
        console.log('🎤 KWSFactory: Creating KWS adapter...');
        
        // Try Porcupine first
        try {
            const porcupineKWS = new PorcupineKWS();
            const success = await porcupineKWS.init(options);
            
            if (success) {
                console.log('🎤 KWSFactory: Using Porcupine KWS');
                return porcupineKWS;
            } else {
                console.log('🎤 KWSFactory: Porcupine failed, falling back to TextFallbackKWS');
            }
        } catch (error) {
            console.warn('🎤 KWSFactory: Porcupine not available, falling back to TextFallbackKWS:', error);
        }
        
        // Fallback to TextFallbackKWS
        try {
            const textFallbackKWS = new TextFallbackKWS();
            const success = await textFallbackKWS.init(options);
            
            if (success) {
                console.log('🎤 KWSFactory: Using TextFallbackKWS');
                return textFallbackKWS;
            } else {
                throw new Error('TextFallbackKWS initialization failed');
            }
        } catch (error) {
            console.error('🎤 KWSFactory: All KWS implementations failed:', error);
            throw new Error('No KWS implementation available');
        }
    }

    /**
     * Check if Porcupine is available
     * @returns Promise<boolean> - True if Porcupine can be used
     */
    static async isPorcupineAvailable() {
        try {
            const porcupineKey = process.env.REACT_APP_PORCUPINE_KEY;
            if (!porcupineKey) {
                return false;
            }
            
            // Try to import Porcupine
            await import('@picovoice/porcupine-web');
            return true;
        } catch (error) {
            return false;
        }
    }
}

export default KWSFactory;
